// Core
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { 
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Input,
  Select,
  Grid,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { MdDelete } from 'react-icons/md';
import { MdAddPhotoAlternate } from 'react-icons/md';
import { GrGallery } from 'react-icons/gr';

// Assets
import { apiService } from 'services/ApiService';
import { textLang } from 'services/Lang';

const UpdateAvatarModal = ({ 
  userData, setUserData,
  isOpen, onOpen, onClose 
}) => {
  const bgUpdateAvatarIcon = useColorModeValue('white', '#0E1633');
  const textBrowseColor = useColorModeValue('black', '#9FABCB');
  const iconDeleteColor = useColorModeValue('white', '#9FABCB');

  const [errorCard, setErrorCard] = useState();
  const [userImage, setUserImage] = useState(userData?.image);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [isDisabledButton, setIsDisabledButton] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (userData?.image) {
      setUserImage(userData?.image);
    }
  }, [userData]);

  const handleClickImage = () => {
    document.getElementById('fileInput').click(); 
  };

  const handleFileChange = (e) => {
    setIsDisabledButton(false);
    const file = e.target.files[0];
    if (!file) { return; }

    setErrorCard();

    if (file.size > 1 * 1024 * 1024) {
      setErrorCard(textLang('file_is_too_large'));
      return;
    }

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setErrorCard(textLang('unsupported_file_type'));
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setUserImage(reader.result);
      //setUserData({ ...userData, image: reader.result }); 
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setIsDisabledButton(false);
    setSelectedFile(null);
    setUserImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; 
    }

    setErrorCard();
  };

  const handleUpdateAvatar = useCallback(async () => {
    setIsLoadingButton(true);
    setErrorCard();

    if (selectedFile) {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('itemType', 'user');
      formData.append('sync', true);

      try {
        const response = await apiService.uploadImage(formData); 
        console.log('File uploaded successfully:', response.data);
        setUserData({ ...userData, image: response.data }); 
        onClose();
        setIsDisabledButton(true);
      } catch (error) {
        setErrorCard(textLang('no_face'));
        console.error('Error uploading file:', error);
      }
    } else {
      if(userData?.image === null && selectedFile === null){
        setErrorCard(textLang('selected_a_photo'));
        setIsLoadingButton(false);
        return;
      }

      try {
        const response = await apiService.deleteImage({itemType: 'user'}); 
        console.log('File uploaded successfully:', response.data);
        setUserData({ ...userData, image: null }); 
        onClose();
      } catch (error) {
        setErrorCard(textLang('no_face'));
        console.error('Error uploading file:', error);
      }
    }

    setIsLoadingButton(false);
  });

  return (
  	<> 
  	<Modal isOpen={isOpen} onClose={isLoadingButton ? () => {} : () => {
        onClose();
        setErrorCard();
        setUserImage(userData?.image);
        setSelectedFile(null);
        setIsDisabledButton(true);
      }}>
      <ModalOverlay />
      <ModalContent className="modal__simple modal__simple_avatar" maxW={{ base: '93%' }} bg='white !important'>
        <ModalBody>

          { userImage ? 
            <Box>
              <Box 
                m="16px auto 0 auto"
                position="relative" 
                overflow="hidden" w="50%" h="260px"
                borderRadius="16px"
                >
                <Box zIndex="1" w="100%" h="260px" bg={`url(${userImage})`} filter="blur(12px)" />
                <Image 
                  zIndex="2"
                  top="0" position="absolute"
                  src={userImage} 
                  objectFit="contain"
                  w="100%" 
                  h="260px" />

                <Button
                  variant="secondary-md" 
                  position="absolute" 
                  top="12px" 
                  zIndex="5"
                  w="48px"
                  h="48px"
                  right="12px" 
                  className="handle-remove-file"
                  onClick={handleRemoveFile}
                  leftIcon={<MdDelete color={iconDeleteColor} fontSize="24px" />} />
              </Box>

              { errorCard && 
                <Text textAlign="center" variant="text-md" color="red" mt="16px" mb="0px">
                  {errorCard}
                </Text> 
              }

              <Box 
                h="44px"
                onClick={handleClickImage} 
                mt="12px"
                cursor="pointer"
                _hover={{
                  borderColor: '#1c5175'
                }}
                borderRadius="16px"
                border="1px dashed #ff4409a6"
                background={bgUpdateAvatarIcon}
                textAlign="center">
                <Flex gap="5px" alignItems="center" justifyContent="center" pt="9px">
                  <MdAddPhotoAlternate fontSize="24px" />
                  <Text variant="title-md">{textLang('upload')}</Text>
                </Flex>
                
              </Box>
            </Box>
            : 
            <Box 
              h="260px"
              onClick={handleClickImage} 
              mt="16px"
              cursor="pointer"
              _hover={{
                borderColor: '#1c5175'
              }}
              mb="8px"
              p="56px 0 28px 0"
              borderRadius="16px"
              border="1px dashed #099bffa6"
              background={bgUpdateAvatarIcon}
              textAlign="center">
              <GrGallery fontSize="88px" color="rgb(255, 65, 51)" style={{margin: '0 auto'}} h="88px" />
              { errorCard && 
                <Text variant="text-md" color="red" mt="8px" mb="-12px">
                  {errorCard}
                </Text> 
              }
              <Text variant="text-md" color={textBrowseColor} p="18px 0 8px 0">{textLang('upload_browse')}</Text>
              <Text variant="text-xs" color="black" pb="5px">Maximum size: <b style={{fontWeight:'500'}}>1 mb</b></Text>
              <Text variant="text-xs" color="black">Supporting Image Type: <b style={{fontWeight:'500'}}>JPG, JPEG, PNG</b></Text>
            </Box>
          }

          <input
            id="fileInput"
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleFileChange}
            ref={fileInputRef}
          />

	        <Grid 
            gap="12px" mt="16px" w="100%"
            templateColumns={{base: '1fr', md: '1fr 1fr'}}>
            <Button
             variant="secondary-md" 
             disabled={isLoadingButton}
             onClick={() => {
              onClose();
              setErrorCard();
              setUserImage(userData?.image);
              setSelectedFile(null);
              setIsDisabledButton(true);
             }}>{textLang('back')}</Button>
	          <Button 
              variant="button-md" 
              onClick={handleUpdateAvatar} 
              disabled={isDisabledButton}
              isLoading={isLoadingButton}>{textLang('confirm')}</Button>
	        </Grid>

          <Box h="16px" />
        </ModalBody>
      </ModalContent>
    </Modal> 
    </>
  );
};

export default UpdateAvatarModal;
